import React, { useMemo, useState } from 'react';
import { ArrowUpDown, ChevronDown, ChevronUp, MessageSquare } from 'lucide-react';
import type { EstimationAccuracyDetailResponse } from '../../../services/apiService';
import { JIRA_BROWSE_PREFIX } from '../../../shared/jiraBrowseUrl';
import { useEstimationUnit } from './estimationUnit';
import { EstimationUnitToggle } from './EstimationUnitToggle';

type SortColumn =
  | 'key'
  | 'fixVersion'
  | 'summary'
  | 'estimate'
  | 'logged'
  | 'deviationHours'
  | 'deviationPct'
  | 'note';
type SortDirection = 'asc' | 'desc';

interface Props {
  detail: EstimationAccuracyDetailResponse;
  issueComments: Record<string, string>;
  openCommentPopup: (key: string, summary: string) => void;
  targetDeviationPct?: number;
}

/** Format hours converted to the active unit. Period summary uses 1 decimal
 *  with sign control left to the caller. */
function formatUnitValue(hours: number, isDays: boolean): string {
  return (isDays ? hours / 8 : hours).toFixed(1);
}

function formatUnitSigned(hours: number, isDays: boolean): string {
  const v = isDays ? hours / 8 : hours;
  const sign = v >= 0 ? '+' : '';
  return `${sign}${v.toFixed(1)}`;
}

/**
 * Period detail block for the Estimation accuracy tab. Renders the period
 * summary card and the per-task table side by side. Both cards share the
 * same Hours / Days toggle (via `EstimationUnitToggle`) so they stay in
 * lockstep with the trend chart and any other consumer of the unit context.
 */
export const EstimationDetailBlock: React.FC<Props> = ({
  detail,
  issueComments,
  openCommentPopup,
  targetDeviationPct = 10,
}) => {
  const { unit, parenSuffix } = useEstimationUnit();
  const isDays = unit === 'days';
  const summary = detail.summary;

  const [sortColumn, setSortColumn] = useState<SortColumn>('deviationHours');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');

  const handleSortColumn = (column: SortColumn) => {
    setSortDirection((prev) => (sortColumn === column ? (prev === 'asc' ? 'desc' : 'asc') : 'asc'));
    setSortColumn(column);
  };

  const sortedStories = useMemo(() => {
    const stories = detail.stories ?? [];
    const mult = sortDirection === 'asc' ? 1 : -1;
    return [...stories].sort((a, b) => {
      switch (sortColumn) {
        case 'key':
          return mult * a.key.localeCompare(b.key);
        case 'fixVersion':
          return mult * (a.fixVersion ?? '').localeCompare(b.fixVersion ?? '');
        case 'summary':
          return mult * a.summary.localeCompare(b.summary);
        case 'estimate':
          return mult * (a.estimate - b.estimate);
        case 'logged':
          return mult * (a.logged - b.logged);
        case 'deviationHours':
          return mult * (a.deviationHours - b.deviationHours);
        case 'deviationPct':
          return mult * ((a.deviationPct ?? 0) - (b.deviationPct ?? 0));
        case 'note': {
          const noteA = issueComments[a.key] ?? '';
          const noteB = issueComments[b.key] ?? '';
          return mult * noteA.localeCompare(noteB);
        }
        default:
          return 0;
      }
    });
  }, [detail.stories, sortColumn, sortDirection, issueComments]);

  const SortableTh = ({ column, label }: { column: SortColumn; label: string }) => (
    <th onClick={() => handleSortColumn(column)} style={{ cursor: 'pointer', userSelect: 'none' }}>
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
        {label}
        {sortColumn === column ? (
          sortDirection === 'asc' ? (
            <ChevronUp size={14} />
          ) : (
            <ChevronDown size={14} />
          )
        ) : (
          <ArrowUpDown size={14} style={{ opacity: 0.5 }} />
        )}
      </span>
    </th>
  );

  return (
    <>
      <div className="estimation-summary-card chart-card">
        <div className="chart-header chart-header--with-toolbar">
          <div className="chart-header__main">
            <h3>Period: {detail.periodLabel}</h3>
            <p>{summary.storyCount} stories · Focus: how much we missed on estimates</p>
          </div>
          <EstimationUnitToggle />
        </div>
        <div className="estimation-summary-grid">
          <div className="estimation-summary-item">
            <span className="estimation-summary-label">Estimated {parenSuffix}</span>
            <span className="estimation-summary-value">
              {formatUnitValue(summary.totalEstimateHours, isDays)}
            </span>
          </div>
          <div className="estimation-summary-item">
            <span className="estimation-summary-label">Logged {parenSuffix}</span>
            <span className="estimation-summary-value">
              {formatUnitValue(summary.totalLoggedHours, isDays)}
            </span>
          </div>
          <div className="estimation-summary-item estimation-summary-deviation">
            <span className="estimation-summary-label">Deviation {parenSuffix}</span>
            <span
              className={`estimation-summary-value deviation-${
                (summary.deviationHours ?? 0) >= 0 ? 'over' : 'under'
              }`}
            >
              {formatUnitSigned(summary.deviationHours ?? 0, isDays)}
            </span>
          </div>
          <div className="estimation-summary-item estimation-summary-deviation">
            <span className="estimation-summary-label estimation-summary-label--accent">
              Deviation (%)
            </span>
            <span
              className={`estimation-summary-value deviation-${
                (summary.deviationPct ?? 0) >= 0 ? 'over' : 'under'
              }`}
            >
              {summary.deviationPct != null
                ? `${summary.deviationPct >= 0 ? '+' : ''}${summary.deviationPct}%`
                : '—'}
            </span>
          </div>
        </div>
        {(summary.deviationPct ?? 0) > targetDeviationPct && (
          <p className="estimation-summary-hint">
            Above {targetDeviationPct}% — work on estimation quality.
          </p>
        )}
      </div>

      <div className="estimation-tasks-card chart-card">
        <div className="chart-header chart-header--with-toolbar">
          <div className="chart-header__main">
            <h3>Tasks in period (worst deviation first)</h3>
            <p>Click column headers to sort; see where we missed the most</p>
          </div>
          <EstimationUnitToggle />
        </div>
        <div className="estimation-table-wrap">
          <table className="estimation-table estimation-tasks-table">
            <thead>
              <tr>
                <SortableTh column="key" label="Key" />
                <SortableTh column="fixVersion" label="Fix Version" />
                <th>Summary</th>
                <SortableTh column="estimate" label={`Estimate ${parenSuffix}`} />
                <SortableTh column="logged" label={`Logged ${parenSuffix}`} />
                <SortableTh column="deviationHours" label={`Deviation ${parenSuffix}`} />
                <SortableTh column="deviationPct" label="Deviation (%)" />
                <th>Note</th>
              </tr>
            </thead>
            <tbody>
              {sortedStories.map((s) => (
                <tr key={s.key}>
                  <td className="estimation-task-key">
                    <a
                      href={`${JIRA_BROWSE_PREFIX}${encodeURIComponent(s.key)}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="task-link"
                    >
                      {s.key}
                    </a>
                  </td>
                  <td className="estimation-task-fixversion">{s.fixVersion}</td>
                  <td className="estimation-task-summary">{s.summary}</td>
                  <td>{formatUnitValue(s.estimate, isDays)}</td>
                  <td>{formatUnitValue(s.logged, isDays)}</td>
                  <td>
                    <span className={s.deviationHours >= 0 ? 'deviation-over' : 'deviation-under'}>
                      {formatUnitSigned(s.deviationHours, isDays)}
                    </span>
                  </td>
                  <td>
                    <span
                      className={(s.deviationPct ?? 0) >= 0 ? 'deviation-over' : 'deviation-under'}
                    >
                      {s.deviationPct != null
                        ? `${s.deviationPct >= 0 ? '+' : ''}${s.deviationPct}%`
                        : '—'}
                    </span>
                  </td>
                  <td className="estimation-comment-td">
                    <button
                      type="button"
                      className={`issue-comment-btn${
                        issueComments[s.key] ? ' issue-comment-btn--has-note' : ''
                      }`}
                      title={issueComments[s.key] ? `Note: ${issueComments[s.key]}` : 'Add note'}
                      onClick={() => openCommentPopup(s.key, s.summary)}
                      aria-label={issueComments[s.key] ? 'Edit note' : 'Add note'}
                    >
                      <MessageSquare size={15} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
};
